import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SlideToConfirm.css';

const CONFIRM_RATIO = 0.88;
const HANDLE_SIZE = 52;
const TRACK_PADDING = 4;

export interface SlideToConfirmProps {
  onConfirm: () => void;
  label?: string;
  confirmedLabel?: string;
  disabled?: boolean;
  /** Change value to reset the slider (e.g. error message). */
  resetKey?: string | number;
  className?: string;
}

const SlideToConfirm: React.FC<SlideToConfirmProps> = ({
  onConfirm,
  label = 'Slide to confirm',
  confirmedLabel = 'Confirmed!',
  disabled = false,
  resetKey = '',
  className = '',
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartX = useRef(0);
  const dragStartOffset = useRef(0);
  const maxOffsetRef = useRef(0);
  const offsetRef = useRef(0);

  const [offset, setOffset] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const updateMaxOffset = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = Math.max(0, track.clientWidth - HANDLE_SIZE - TRACK_PADDING * 2);
    maxOffsetRef.current = max;
    setMaxOffset(max);
  }, []);

  useEffect(() => {
    updateMaxOffset();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(updateMaxOffset);
    ro.observe(track);
    return () => ro.disconnect();
  }, [updateMaxOffset]);

  useEffect(() => {
    offsetRef.current = 0;
    setOffset(0);
    setConfirmed(false);
    isDraggingRef.current = false;
    setIsDragging(false);
  }, [resetKey]);

  useEffect(() => {
    if (disabled && !confirmed) {
      offsetRef.current = 0;
      setOffset(0);
      isDraggingRef.current = false;
      setIsDragging(false);
    }
  }, [disabled, confirmed]);

  const clampOffset = useCallback((value: number) => {
    return Math.min(Math.max(0, value), maxOffsetRef.current);
  }, []);

  const finishDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);

    const max = maxOffsetRef.current;
    if (max <= 0) {
      offsetRef.current = 0;
      setOffset(0);
      return;
    }

    const current = offsetRef.current;
    const ratio = current / max;
    if (ratio >= CONFIRM_RATIO && !disabled) {
      offsetRef.current = max;
      setOffset(max);
      setConfirmed(true);
      onConfirm();
      return;
    }

    offsetRef.current = 0;
    setOffset(0);
  }, [disabled, onConfirm]);

  const handlePointerMove = useCallback(
    (clientX: number) => {
      if (!isDraggingRef.current || confirmed || disabled) return;
      const delta = clientX - dragStartX.current;
      const nextOffset = clampOffset(dragStartOffset.current + delta);
      offsetRef.current = nextOffset;
      setOffset(nextOffset);
    },
    [clampOffset, confirmed, disabled],
  );

  useEffect(() => {
    if (!isDragging) return;

    const onPointerMove = (e: PointerEvent) => {
      handlePointerMove(e.clientX);
    };

    const onPointerEnd = () => {
      finishDrag();
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
    };
  }, [isDragging, handlePointerMove, finishDrag]);

  const onHandlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (confirmed || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    updateMaxOffset();
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartOffset.current = offsetRef.current;
  };

  const progress = confirmed ? 1 : maxOffset > 0 ? Math.min(1, offset / maxOffset) : 0;

  const trackStyle = {
    '--slide-progress': String(progress),
  } as React.CSSProperties;

  return (
    <div
      className={`slide-to-confirm${confirmed ? ' slide-to-confirm--confirmed' : ''}${
        isDragging ? ' slide-to-confirm--dragging' : ''
      }${disabled ? ' slide-to-confirm--disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={trackRef}
      style={trackStyle}
      role="group"
      aria-label={confirmed ? confirmedLabel : label}
    >
      <span className="slide-to-confirm__label">
        {confirmed ? confirmedLabel : label}
      </span>
      <button
        type="button"
        className="slide-to-confirm__handle"
        style={{
          transform: `translateX(${confirmed ? maxOffset : offset}px)`,
        }}
        disabled={disabled || confirmed}
        aria-label={confirmed ? confirmedLabel : label}
        onPointerDown={onHandlePointerDown}
      >
        {confirmed ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <polyline
              points="20 6 9 17 4 12"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <polyline
              points="9 6 15 12 9 18"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      <span className="visually-hidden" aria-live="polite">
        {confirmed ? confirmedLabel : `${Math.round(progress * 100)}%`}
      </span>
    </div>
  );
};

export default SlideToConfirm;
